/* eslint-disable @typescript-eslint/no-explicit-any */
import { CircularProgress, makeStyles } from "@material-ui/core";
import {
  CloudDownload,
  CloudUpload,
  Gavel,
  Group,
  RestorePage,
  DataUsage,
  Replay,
  Ballot,
  MoreVert,
  Warning,
  RateReview,
} from "@material-ui/icons";
import ErrorOutlinedIcon from "@material-ui/icons/ErrorOutlined";
import {
  Box,
  Button,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQueryClient,
} from "@tanstack/react-query";
import { cardTitle } from "assets/jss/material-dashboard-pro-react";
import ApprovalModal from "components/Modals/ApprovalModal";
import ExpirationDateModal from "components/Modals/ExpirationDateModal";
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import CardHeader from "components/UI/Card/CardHeader";
import CardIcon from "components/UI/Card/CardIcon";
import CheckDocumentsModal from "components/Modals/CheckDocumentsModal";
import CustomPopover, {
  CustomPopoverRef,
} from "components/UI/CustomPopover/CustomPopover";
import PopoverButton from "components/UI/CustomPopover/PopoverButton";
import GridItemTS from "components/UI/Grid/GridItemTS";
import LogSystemModal from "components/Modals/LogSystemModal";
import ReactTable from "components/Tables/ReactTable/ReactTable";
import RejectedModal from "components/Modals/RejectedModal";
import UploadModal, { UploadModalType } from "components/Modals/UploadModal";
import ConfirmReopenModal from "components/Modals/UploadModal/ConfirmReopenModal";
import { parse, parseISO, format, isValid } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ActivityItemConfig } from "services/input-core/activity-items/@types";
import { useActivityItemConfig } from "services/input-core/activity-items/get-activity-item-config";
import { useResetActivityItem } from "services/input-core/activity-items/reset-activity-item";
import { useResetActivityItemValidation } from "services/input-core/activity-items/reset-validation";
import { useReviewActivityItemValidation } from "services/input-core/activity-items/review-activity-item";
import { useSuperUserManualValidation } from "services/input-core/manual-validation/super-user-validation";
import { useWorkflowProcess } from "services/input-core/workflow/set-workflow-process";
import { useSowContext } from "store/sow";
import {
  ActionTypes,
  ActivityItemStatusEnum,
  ActivityItemValidationStatusEnum,
  FlowStepsStatusEnum,
  RolesEnum,
  VisibleActivityEnum,
} from "utils/@types";
import {
  checkMessageErrors,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
  getBadgeFromStatus,
} from "utils/functions";
import {
  Delete,
  DocumentScannerRounded,
  TableChart,
} from "@mui/icons-material";
import { useDeleteActivityItem } from "services/input-core/activity-items/delete-activity-item";
import DeleteActivityItemModal from "components/Modals/ConfirmActivityDelete";
import { useClientConfig } from "hooks/useClientConfig";
import { useWorkItemManualValidation } from "services/input-core/work-items/manual-validate";
import AnalysisModal from "components/Modals/AnalysisModal/AnalysisModal";
import ManualValidationModal from "components/Modals/ManualValidationModal/ManualValidationModal";
import useHasRole from "hooks/useHasRole";
import { useLanguageSettings } from "store/user-settings/user-settings-store";
import { NewModalDocuments } from "components/Modals/NewModalDocuments";
import { NewModalAnalysis } from "components/Modals/NewModalAnalysis";
import { useActivityItemContext } from "store/activityItem";
import { ActivityItem } from "store/activityItem/activityItemContext.types";
import ConfirmReviewActivityModal from "components/Modals/ConfirmReviewActivityModal";
import { useSubscription } from "store/auth/auth-store";
import { customFilter } from "helpers/customFilter";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const styles: any = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  cardStatusText: {
    marginTop: "10px",
    marginBottom: "0px",
    fontWeight: "400",
    lineHeight: "1",
  },
  cardSelect: {
    width: "200px",
    marginBottom: "10px",
    fontWeight: "400",
    lineHeight: "1",
  },
  cardBadge: {
    marginTop: "5px",
    marginBottom: "5px",
    paddingLeft: "10px",
  },
  cardStatusBlock: {
    width: "600px",
    display: "flex",
  },
};

const useStyles = makeStyles(styles);

interface ActivityItemsSupplierProps {
  refID?: string;
  refetchActivityItems: (
    options?: RefetchOptions & RefetchQueryFilters<unknown>,
  ) => Promise<QueryObserverResult<any, unknown>>;
  profile: "WORKER" | "SOW";
  isFetched?: boolean;
  entityDate?: {
    start: string;
    end: string;
  };
  supplierCode?: string;
}

type ReactTableProps = {
  columns: any;
  data: any[];
  filters?: any;
  setFilters?: any;
  sortingInitial?: any;
  setSortingInitial?: any;
  cacheFilters?: boolean;
  "data-cy"?: string;
  id?: string;
  columnsNotTrimFilters?: string[];
};

export default function BeltrameActivityItemTable({
  refID,
  profile,
  refetchActivityItems,
  entityDate,
  supplierCode,
}: ActivityItemsSupplierProps): JSX.Element {
  const { keycloak } = useKeycloak();
  const { hasRealmRole } = useHasRole();
  const { language } = useLanguageSettings();

  const { isKuo } = useSubscription();

  const isStratesysUser = keycloak.hasRealmRole("stratesys");
  const isReopener = keycloak.hasRealmRole("Reopen Activity");
  const isSuperUser = keycloak.hasRealmRole("superUser");
  const isApprover = keycloak.hasRealmRole("approver");
  const isOnlyBuyer =
    keycloak.hasResourceRole("buyer", "uverify") && !isApprover && !isSuperUser;
  const isSupplier = keycloak.hasResourceRole("supplier", "uverify");

  const [searchParams] = useSearchParams();
  const activityId = searchParams.get("activity");
  const month = searchParams.get("month");
  const year = searchParams.get("year");

  const urlValues: boolean =
    activityId?.length > 0 && month?.length > 0 && year?.length > 0;

  const {
    activityItems,
    currentActivityItem,
    isLoading: isActivityItemsLoading,
    // isRefetching: isActivityItemsRefetching,
    selectActivity,
  } = useActivityItemContext((state) => ({
    activityItems: state.filteredActivityItems,
    currentActivityItem: state.currentActivity,
    isLoading: state.isLoading,
    isRefetching: state.isRefetching,
    selectActivity: state.selectActivity,
  }));

  const getActivitySelected = useSowContext((ctx) => ctx.getActivitySelected);
  const getActivityFlow = useSowContext((ctx) => ctx.getActivityFlow);
  const enableActivityItemProfile = useSowContext(
    (ctx) => ctx.enableActivityItemProfile,
  );
  const isEnabled = useSowContext((ctx) => ctx.isEnabled);
  const [alignment, setAlignment] = useState(
    isEnabled ? "enabled" : "disabled",
  );

  const { viewActivities } = useClientConfig();

  const [activeFilterDueDateURL, setActiveFilterDueDateURL] = useState(
    viewActivities === VisibleActivityEnum.FULL && urlValues,
  );

  const shouldRenderToggleButtonGroup =
    (isStratesysUser && isOnlyBuyer) ||
    (isStratesysUser && isSupplier) ||
    (isStratesysUser && isReopener) ||
    (isStratesysUser && isApprover);

  const [filters, setFilters] = useState(
    [].concat(
      activityId
        ? [
            {
              id: "code",
              value: activityId || undefined,
            },
          ]
        : [],
      activeFilterDueDateURL
        ? [
            {
              id: "activityItemDueDate",
              value: `${month}/${year}` || undefined,
            },
          ]
        : [],
    ),
  );

  useEffect(() => {
    setActiveFilterDueDateURL(
      () => viewActivities === VisibleActivityEnum.FULL && urlValues,
    );
  }, [viewActivities]);

  useEffect(() => {
    if (activeFilterDueDateURL) {
      if (filters.some((item) => item.id === "activityItemDueDate")) {
        return setFilters((filters) =>
          filters.map((aux) =>
            aux.id === "activityItemDueDate"
              ? { ...aux, value: `${month}/${year}` || undefined }
              : aux,
          ),
        );
      } else {
        return setFilters((filters) =>
          filters.concat([
            {
              id: "activityItemDueDate",
              value: `${month}/${year}`,
            },
          ]),
        );
      }
    } else {
      if (filters.some((item) => item.id === "activityItemDueDate")) {
        return setFilters((filters) =>
          filters.filter((filter) => filter.id !== "activityItemDueDate"),
        );
      }
    }
  }, [activeFilterDueDateURL]);

  const [sortingInitial, setSortingInitial] = useState([
    {
      id: "modifiedAt",
      desc: false,
    },
  ]);

  const classes = useStyles();
  const queryClient = useQueryClient();

  const { t } = useTranslation([
    "activity-item-table",
    "list-errors",
    "list-messages",
  ]);

  const handleToggle = (event: any, newAlignment: any) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment);
      enableActivityItemProfile(newAlignment === "enabled");
    }
  };

  const columnsStatus = useMemo(() => {
    const columns = [
      {
        Header: t("activity-code"),
        accessor: "code",
        Cell: ({ value, row }: any) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {value}
            {row.original.accessRestriction && (
              <Tooltip title={t("access-restriction")}>
                <IconButton
                  size="small"
                  color="warning"
                  style={{ marginLeft: 4 }}
                >
                  <Warning style={{ fontSize: "1.2rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        Header: t("action"),
        accessor: "action",
      },
      {
        Header: t("created-at"),
        accessor: "date",
      },
      {
        Header: t("fg-status"),
        accessor: "status",
        filter: customFilter,
      },
      {
        Header: t("uv-status"),
        accessor: "result",
        filter: customFilter,
      },
      {
        Header: t("date"),
        accessor: "activityItemDueDate",
      },
      {
        Header: t("expiration-date"),
        accessor: "validationExpirationDate",
      },
      {
        Header: t("modified-at"),
        accessor: "modifiedAt",
      },
      {
        Header: t("actions"),
        accessor: "actions",
      },
    ];

    const columnsKuo = [
      {
        Header: t("fg-status"),
        accessor: "status",
        filter: customFilter,
      },
      {
        Header: t("action"),
        accessor: "action",
      },
      {
        Header: t("activity-code"),
        accessor: "code",
        Cell: ({ value, row }: any) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {value}
            {row.original.accessRestriction && (
              <Tooltip title={t("access-restriction")}>
                <IconButton
                  size="small"
                  color="warning"
                  style={{ marginLeft: 4 }}
                >
                  <Warning style={{ fontSize: "1.2rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        Header: t("created-at"),
        accessor: "date",
      },
      {
        Header: t("uv-status"),
        accessor: "result",
        filter: customFilter,
      },
      {
        Header: t("date"),
        accessor: "activityItemDueDate",
      },
      {
        Header: t("processed-at"),
        accessor: "modifiedAt",
      },
      {
        Header: t("actions"),
        accessor: "actions",
      },
    ];

    return isKuo ? columnsKuo : columns;
  }, [language, isKuo]);

  const {
    mutate: superManualValidation,
    isLoading: isSavingSuperManualValidation,
  } = useSuperUserManualValidation();

  const { mutate: submitManualValidation } = useWorkItemManualValidation();

  const {
    mutate: resetActivityValidation,
    isLoading: isResetingActivityValidation,
  } = useResetActivityItemValidation();

  const {
    mutate: reviewActivityItem,
    isLoading: isReviewActivityItem,
  } = useReviewActivityItemValidation();

  const {
    data: activityConfig,
  }: { data: ActivityItemConfig[] } = useActivityItemConfig();

  const {
    mutate: resetActivityItem,
    isLoading: isResetingActivityItem,
  } = useResetActivityItem();

  const {
    mutate: deleteActivityItem,
    isLoading: isDeletingActivityItem,
  } = useDeleteActivityItem();

  const {
    mutate: workflowProcess,
    isLoading: isWorkflowProcess,
  } = useWorkflowProcess();

  const [modalOpen, setModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [checkDocumentsModalOpen, setCheckDocumentsModalOpen] = useState(false);
  const [checkReopenModalOpen, setCheckReopenModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);
  const [expirationDateModalOpen, setExpirationDateModalOpen] = useState(false);
  const [logSystemModalOpen, setLogSystemModalOpen] = useState(false);
  const [
    deleteActivityItemModalOpen,
    setDeleteActivityItemModalOpen,
  ] = useState(false);

  const [analysisModal, setAnalysisModal] = useState({
    open: false,
    content: undefined,
  });

  const [manualValidationModal, setManualValidationModal] = useState({
    open: false,
    _id: undefined,
    activityItemCode: undefined,
    fieldglassId: undefined,
  });

  const [newAnalysisModal, setNewAnalysisModal] = useState({
    open: false,
    title: null,
    subtitle: null,
  });

  const [newCheckDocumentsModal, setNewCheckDocumentsModal] = useState({
    open: false,
    title: null,
    subtitle: null,
  });

  const [rejectedReason, setRejectedReason] = useState(null);

  const customPopoverRef = useRef<CustomPopoverRef>(null);

  const closePopoverFromParent = () => {
    if (customPopoverRef.current) {
      customPopoverRef.current.closePopover();
    }
  };

  const isFirstStep = (activityItem: ActivityItem) => {
    return (
      activityItem?.validationStep === undefined ||
      activityItem?.validationStep === null ||
      (activityItem?.validationStep === 0 &&
        activityItem?.validationStatus !==
          ActivityItemValidationStatusEnum.REJECTED)
    );
  };

  const handleSuperUserApproval = (
    activityItem: ActivityItem,
    validationExpirationDate: Date,
    reason: string,
    hasInfiniteLifetime = false,
    validationExpirationStartDate?: Date,
  ) => {
    activityItem?.hasSuperUserManualCompletion
      ? superManualValidation(
          {
            reason,
            fieldglassId: activityItem.fieldglassId,
            activityItemCode: activityItem.activityItemCode,
            subscriptionId: activityItem.subscription,
            zIdentifier: activityItem.zIdentifier,
            ...(hasInfiniteLifetime
              ? {
                  hasInfiniteLifetime,
                }
              : {
                  validationExpirationDate,
                  ...(activityItem?.hasStartDate && {
                    validationExpirationStartDate,
                  }),
                }),
          },
          {
            onSuccess: () => {
              toast.success(t("success-validate", { ns: "list-messages" }));

              queryClient.invalidateQueries(["activity-items-sow", refID]);
              setExpirationDateModalOpen(false);
            },
            onError: (error: any) => {
              toast.error(
                t(
                  checkMessageErrors(
                    error?.response?.data?.error,
                    "ERR_VALIDATE_DEFAULT",
                  ),
                  { ns: "list-errors" },
                ),
              );
            },
          },
        )
      : submitManualValidation(
          {
            workItemID: activityItem?.workitems[0]?._id,
            reason,
            activityItemCode: activityItem.activityItemCode,
            fieldglassId: activityItem.fieldglassId,
            ...(activityItem?.workitems[0]?._id
              ? {}
              : {
                  zIdentifier: activityItem.zIdentifier,
                  profile: profile,
                  supplierCode: supplierCode,
                }),
            ...(hasInfiniteLifetime
              ? {
                  hasInfiniteLifetime,
                }
              : {
                  validationExpirationDate,
                  ...(activityItem?.hasStartDate && {
                    validationExpirationStartDate,
                  }),
                }),
          },
          {
            onSuccess: () => {
              toast.success(
                t("success-manual-validation", {
                  ns: "manual-validation-modal",
                }),
                {
                  className: "success-toast-message",
                },
              );
              queryClient.invalidateQueries(["activity-items-sow", refID]);
              setExpirationDateModalOpen(false);
            },
            onError: (error: any) => {
              toast.error(
                t(
                  checkMessageErrors(
                    error?.response?.data?.error,
                    "ERR_MANUAL_VALIDATION_DEFAULT",
                  ),
                  { ns: "list-errors" },
                ),
              );
            },
          },
        );
  };

  const handleResetActivityItem = (activityItemCode: any) => {
    toast.success(t("info-reset-started", { ns: "list-messages" }));

    resetActivityItem(
      {
        zIdentifier: activityItemCode.zIdentifier,
        activityItemCode: activityItemCode.activityItemCode,
        fieldglassId: activityItemCode.fieldglassId,
      },
      {
        onSuccess: () => {
          toast.success(t("success-reset", { ns: "list-messages" }));

          refetchActivityItems();
        },
        onError: () => {
          toast.error(t("reset-fail"));
        },
      },
    );
  };

  const handleResetActivityValidation = (currentActivityItem: ActivityItem) => {
    toast.info(`
      ${t("info-redefining-validation", {
        ns: "list-messages",
      })}${currentActivityItem.activityItemCode}`);

    resetActivityValidation(
      {
        zIdentifier: currentActivityItem.zIdentifier,
        activityItemCode: currentActivityItem.activityItemCode,
        fieldglassId: currentActivityItem.fieldglassId,
      },
      {
        onSuccess: () => {
          toast.success(t("success-reopen", { ns: "list-messages" }));

          refetchActivityItems();
          queryClient.invalidateQueries(["activity-items-sow", refID]);
          setCheckReopenModalOpen(false);
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_REOPEN_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleReviewActivityValidation = (
    currentActivityItem: ActivityItem,
    reason: string,
  ) => {
    toast.info(`
      ${t("info-redefining-activity", {
        ns: "list-messages",
      })}${currentActivityItem.activityItemCode}`);

    reviewActivityItem(
      {
        zIdentifier: currentActivityItem.zIdentifier,
        activityItemCode: currentActivityItem.activityItemCode,
        fieldglassId: currentActivityItem.fieldglassId,
        reviewReason: reason,
      },
      {
        onSuccess: () => {
          toast.success(t("success-review", { ns: "list-messages" }));

          refetchActivityItems();
          queryClient.invalidateQueries(["activity-items-sow", refID]);
          setReviewModalOpen(false);
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_REVIEW_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleWorkflowProcess = (activityItemId: string) => {
    toast.info(t("info-process-activity", { ns: "list-messages" }));
    workflowProcess(
      {
        activityItemId: activityItemId,
      },
      {
        onSuccess: () => {
          toast.success(t("success-process-activity", { ns: "list-messages" }));

          queryClient.invalidateQueries(["activity-items-sow", refID]);
        },
        onError: (error: any) => {
          toast.warning(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_PROCESS_ACTIVITY_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleDeleteActivityItem = (reason: string) => {
    toast.success(t("info-delete-started", { ns: "list-messages" }));

    deleteActivityItem(
      {
        id: currentActivityItem?._id,
        reason: reason,
      },
      {
        onSuccess: () => {
          toast.success(t("success-delete", { ns: "list-messages" }));
          refetchActivityItems();
          setDeleteActivityItemModalOpen(false);
        },
        onError: () => {
          toast.error(t("fail-delete", { ns: "list-messages" }));
          setDeleteActivityItemModalOpen(false);
        },
      },
    );
  };

  const renderButtonsByStepAndRoles = (
    activityItem: ActivityItem,
    index: number,
  ) => {
    const buttons = [] as JSX.Element[];

    if (!activityConfig) return buttons;

    const config = activityConfig.find(
      (config) => config.mapping === activityItem.activityItemCode,
    );

    if (!config || activityItem?.actions?.length <= 0) return buttons;

    if (activityItem?.actions?.includes(ActionTypes.HISTORY_DOCUMENTS)) {
      buttons.push(
        <PopoverButton
          onClick={() => {
            selectActivity(activityItem);
            setNewCheckDocumentsModal((fallback) => ({
              ...fallback,
              open: true,
            }));
            closePopoverFromParent();
          }}
        >
          <DocumentScannerRounded style={{ fontSize: "1.2rem" }} />
          {t("document(s)")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.APPROVE_ACTIVITY)) {
      buttons.push(
        <PopoverButton
          onClick={() => {
            selectActivity({
              ...activityItem,
              hasStartDate: config?.hasStartDate,
            });
            setApprovalModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="approve-modal-button"
        >
          <Gavel style={{ fontSize: "1.2rem" }} />
          {t("Validate Documents")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.RESET_ACTIVITY)) {
      buttons.push(
        <PopoverButton
          color="primary"
          onClick={() => {
            handleResetActivityItem(activityItem);
            closePopoverFromParent();
          }}
          disabled={isResetingActivityItem}
          data-cy="reset-activity-button"
        >
          <>
            <RestorePage style={{ fontSize: "1.2rem" }} />
            {t("Reset Activity")}
          </>
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.UPLOAD_DOCUMENTS)) {
      buttons.push(
        <PopoverButton
          key="upload_documents"
          onClick={() => {
            selectActivity({
              ...activityItem,
              hasExpirationDate: config?.hasExpirationDate,
              hasStartDate: config?.hasStartDate,
            });
            setModalOpen(true);
            setUploadType(true);
            closePopoverFromParent();
          }}
          data-cy="upload-documents-button"
        >
          <>
            <CloudUpload style={{ fontSize: "1.2rem" }} />
            {t("upload-documents")}
          </>
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.DOWNLOAD_DOCUMENTS)) {
      buttons.push(
        <PopoverButton
          onClick={() => {
            selectActivity(activityItem);
            setModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="download-documents-button"
        >
          <>
            <CloudDownload style={{ fontSize: "1.2rem" }} />
            {t("download-documents")}
          </>
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.REASON)) {
      buttons.push(
        <PopoverButton
          key="reason"
          onClick={() => {
            setRejectedReason(activityItem);
            setRejectedModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="reason-button"
        >
          <ErrorOutlinedIcon style={{ fontSize: "1.2rem" }} />
          {t("rejected")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.SUPER_APPROVE_ACTIVITY)) {
      buttons.push(
        <PopoverButton
          onClick={() => {
            selectActivity({
              ...activityItem,
              hasStartDate: config?.hasStartDate,
            });
            setExpirationDateModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="approve-documents-button"
        >
          <Gavel style={{ fontSize: "1.2rem" }} />
          {t("approve-documents")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.ANALYSIS)) {
      hasRealmRole([RolesEnum.NEW_ANALYSIS_MODAL])
        ? buttons.push(
            <PopoverButton
              onClick={() => {
                selectActivity(activityItem);
                setNewAnalysisModal((fallback) => ({
                  ...fallback,
                  open: true,
                }));
                closePopoverFromParent();
              }}
              disabled={!activityItem?.workitems[0]?.analyzes?.length}
            >
              <TableChart style={{ fontSize: "1.2rem" }} />
              {t("analysis")}
            </PopoverButton>,
          )
        : buttons.push(
            <Tooltip title={t("analysis")}>
              <PopoverButton
                data-cy="analysis-modal-open-button"
                onClick={() => {
                  setAnalysisModal({
                    content: activityItem?.workitems[0]?.analyzes,
                    open: true,
                  });
                  closePopoverFromParent();
                }}
                disabled={!activityItem?.workitems[0]?.analyzes?.length}
              >
                <TableChart style={{ fontSize: "1.2rem" }} />
                {t("analysis")}
              </PopoverButton>
            </Tooltip>,
          );
    }

    const postDividerButtons = [] as JSX.Element[];

    if (activityItem?.actions?.includes(ActionTypes.REOPEN)) {
      postDividerButtons.push(
        <PopoverButton
          disabled={isResetingActivityValidation}
          onClick={() => {
            selectActivity(activityItem);
            setCheckReopenModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="reopen-button"
        >
          <RestorePage style={{ fontSize: "1.2rem" }} />
          {t("reOpen")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.REVIEW_ACTIVITY)) {
      postDividerButtons.push(
        <PopoverButton
          disabled={isReviewActivityItem}
          onClick={() => {
            selectActivity(activityItem);
            setReviewModalOpen(true);
            closePopoverFromParent();
          }}
          // data-cy="review-button"
        >
          <RateReview style={{ fontSize: "1.2rem" }} />
          {t("ReView")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.DELETE_ACTIVITY)) {
      postDividerButtons.push(
        <PopoverButton
          disabled={isDeletingActivityItem}
          onClick={() => {
            selectActivity(activityItem);
            setDeleteActivityItemModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="delete-button"
        >
          <Delete style={{ fontSize: "1.2rem" }} />
          {t("delete")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.PROCESS_ACTIVITY)) {
      postDividerButtons.push(
        <PopoverButton
          disabled={isWorkflowProcess}
          onClick={() => {
            handleWorkflowProcess(activityItem?._id);
            closePopoverFromParent();
          }}
          data-cy="process-activity-button"
        >
          <Replay style={{ fontSize: "1.2rem" }} />
          {t("process-activity")}
        </PopoverButton>,
      );
    }

    if (activityItem?.actions?.includes(ActionTypes.LOG_SYSTEM)) {
      postDividerButtons.push(
        <PopoverButton
          onClick={() => {
            selectActivity(activityItem);
            setLogSystemModalOpen(true);
            closePopoverFromParent();
          }}
          data-cy="log-system-button"
        >
          <DataUsage style={{ fontSize: "1.2rem" }} />
          {t("log-system")}
        </PopoverButton>,
      );
    }

    if (buttons.length > 0 && postDividerButtons.length > 0) {
      buttons.push(<Divider sx={{ width: "90%", marginX: "auto" }} />);
    }

    const allButtons = [...buttons, ...postDividerButtons];

    return (
      <CustomPopover
        ref={customPopoverRef}
        identifier={`activity-item-${index}`}
        data-cy={`popover-button-work-item-${index}`}
      >
        {allButtons}
      </CustomPopover>
    );
  };

  const formattedActivityItems = activityItems?.map(
    (item: any, index: number) => {
      return {
        code: item?.activityItemCode,
        accessRestriction: item?.accessRestriction,
        action: item?.activityItemAction ?? "",
        activityItemDescription: item?.activityItemDescription ?? "",
        documentAmount: 0,
        status: getBadgeFromStatus(
          (() => {
            if (item.isInRetryQueue) {
              return ActivityItemValidationStatusEnum.WAITING_FOR_UPDATE;
            } else {
              return item.activityItemStatus;
            }
          })(),
          (text: string) => t(text, { ns: "status" }),
        ),
        result: !item?.approvedBySuperUser
          ? getBadgeFromStatus(
              (() => {
                const activity = activityConfig?.find(
                  (config) => config.mapping === item.activityItemCode,
                );

                if (!activityConfig) return ActivityItemStatusEnum.READ_ONLY;

                if (!activity || !activity?.hasDocument)
                  return ActivityItemStatusEnum.READ_ONLY;

                if (isKuo)
                  return (
                    item.validationStatus ||
                    ActivityItemValidationStatusEnum.VALIDATION_PENDING
                  );

                if (
                  compareStringsIgnoreCaseAndRemoveSpecialChars(
                    item?.validationStatus,
                    FlowStepsStatusEnum.SUPPLIER_UPLOADING_DOCUMENTS,
                  )
                ) {
                  return ActivityItemStatusEnum.UPLOADING_DOCUMENTS_SUPPLIER;
                }

                if (isFirstStep(item)) {
                  const waitingForDownloadStep = activity?.validationSteps?.find(
                    (step) =>
                      compareStringsIgnoreCaseAndRemoveSpecialChars(
                        step?.name,
                        FlowStepsStatusEnum.CHANGE_ACTIVITY_STATUS,
                      ),
                  );

                  if (waitingForDownloadStep) {
                    return (
                      t(waitingForDownloadStep?.executedByRole[0]) +
                      " " +
                      t("Uploading Documents", { ns: "status" })
                    );
                  }
                }

                if (item?.validationStep) {
                  const configForValidationStep = activity?.validationSteps?.find(
                    (step) => step?.step === item?.validationStep,
                  );

                  if (
                    compareStringsIgnoreCaseAndRemoveSpecialChars(
                      configForValidationStep?.name,
                      FlowStepsStatusEnum.CHANGE_ACTIVITY_STATUS,
                    )
                  ) {
                    return (
                      t(configForValidationStep?.executedByRole[0]) +
                      " " +
                      t("Uploading Documents", { ns: "status" })
                    );
                  }
                }

                return (
                  item?.validationStatus ??
                  ActivityItemStatusEnum.UPLOADING_DOCUMENTS
                );
              })(),
              (text: string) => t(text, { ns: "status" }),
              item?.validationExpirationDate,
              undefined,
              item?.manualValidationOwner,
              item?.validationExpirationStartDate,
            )
          : getBadgeFromStatus(
              item.validationStatus,
              (text: string) => t(text, { ns: "status" }),
              item?.validationExpirationDate,
              item?.approvedBySuperUser,
              item?.manualValidationOwner,
              item?.validationExpirationStartDate,
            ),
        activityItemDueDate: item?.activityItemDueDate
          ? (() => {
              const parsedDate = item?.activityItemDueDate.includes("T")
                ? parseISO(item?.activityItemDueDate)
                : parse(item?.activityItemDueDate, "dd/MM/yyyy", new Date());
              return isValid(parsedDate)
                ? format(parsedDate, "dd/MM/yyyy")
                : "-";
            })()
          : "",
        validationExpirationDate: item?.validationExpirationDate
          ? (() => {
              const [year, month, day] = item.validationExpirationDate
                .split("T")[0]
                .split("-");
              return `${day}/${month}/${year}`;
            })()
          : "",
        date: item?.activityItemCreateDate ?? "-",
        modifiedAt: item?.modifiedAt
          ? (() => {
              const parsedDate = parseISO(item?.modifiedAt);
              return isValid(parsedDate)
                ? format(parsedDate, "dd/MM/yyyy HH:mm:ss")
                : "-";
            })()
          : "",
        actions: (
          <div
            style={{
              display: "flex",
              width: "100%",
              minHeight: item?.actions?.length <= 0 ? "3rem" : "",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              transition: "ease-in-out",
            }}
          >
            {isEnabled ? (
              <Button
                onClick={() => {
                  const activity = activityConfig?.find(
                    (config) => config.mapping === item.activityItemCode,
                  );
                  getActivityFlow(activity);
                  getActivitySelected(item);
                }}
              >
                <Ballot style={{ fontSize: "1.2rem" }} />
              </Button>
            ) : (
              !(item?.actions?.length <= 0) &&
              renderButtonsByStepAndRoles(item, index)
            )}
          </div>
        ),
      };
    },
  );

  const ReactTableWrapper: React.FC<ReactTableProps> = useCallback(
    (props) => {
      return <ReactTable {...props} />;
    },
    [language],
  );

  return (
    <GridItemTS
      key="beltrame-activity-item-table-grid"
      xs={12}
      data-cy="beltrame-activity-item-table"
    >
      {checkDocumentsModalOpen && (
        <CheckDocumentsModal
          onClose={() => setCheckDocumentsModalOpen(false)}
          modalOpen={checkDocumentsModalOpen}
          fieldglassId={refID}
          data-cy="check-documents-modal"
        />
      )}

      {checkReopenModalOpen && (
        <ConfirmReopenModal
          isDeleting={isResetingActivityValidation}
          onClose={() => {
            refetchActivityItems();
            setCheckReopenModalOpen(false);
          }}
          modalOpen={checkReopenModalOpen}
          onConfirm={() => handleResetActivityValidation(currentActivityItem)}
          data-cy="confirm-reopen-modal"
        />
      )}

      {modalOpen && (
        <UploadModal
          profile={profile}
          modalOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          fieldglassId={refID}
          uploadType={uploadType}
          setUploadType={setUploadType}
          entityDate={entityDate}
          type={isKuo ? UploadModalType.KUO : UploadModalType.DEFAULT}
          data-cy="upload-modal"
        />
      )}

      {rejectedModalOpen && (
        <RejectedModal
          modalOpen={rejectedModalOpen}
          onClose={() => {
            setRejectedModalOpen(false);
            refetchActivityItems();
          }}
          rejectedReason={rejectedReason}
          data-cy="rejected-modal"
        />
      )}

      {approvalModalOpen && (
        <ApprovalModal
          profile={profile}
          modalOpen={approvalModalOpen}
          onClose={() => {
            setApprovalModalOpen(false);
            refetchActivityItems();
          }}
          fieldglassId={refID}
          entityDate={entityDate}
          data-cy="approval-modal"
        />
      )}

      {logSystemModalOpen && (
        <LogSystemModal
          modalOpen={logSystemModalOpen}
          onClose={() => {
            setLogSystemModalOpen(false);
          }}
          data-cy="log-system-modal"
        />
      )}

      {expirationDateModalOpen && (
        <ExpirationDateModal
          isUploading={isSavingSuperManualValidation}
          modalOpen={expirationDateModalOpen}
          onClose={() => {
            setExpirationDateModalOpen(false);
            refetchActivityItems();
          }}
          entityDate={entityDate}
          onConfimSuperUser={handleSuperUserApproval}
          hasReason={true}
          hasApprovalWithoutExpiration={true}
          data-cy="expiration-date-modal"
        />
      )}

      {deleteActivityItemModalOpen && (
        <DeleteActivityItemModal
          onConfirm={(reason) => {
            handleDeleteActivityItem(reason);
          }}
          isDeleting={isDeletingActivityItem}
          onClose={() => {
            setDeleteActivityItemModalOpen(false);
            refetchActivityItems();
          }}
          modalOpen={deleteActivityItemModalOpen}
          data-cy="delete-activity-item-modal"
        />
      )}

      {analysisModal.open && (
        <AnalysisModal
          data-cy="analysis-modal"
          analysis={analysisModal.content}
          handleClose={() => {
            setAnalysisModal({
              open: false,
              content: undefined,
            });
            refetchActivityItems();
          }}
          modal={analysisModal.open}
        />
      )}

      {manualValidationModal.open && (
        <ManualValidationModal
          data-cy="manual-validation-modal"
          open={manualValidationModal.open}
          OnSuccess={() => refetchActivityItems()}
          OnFail={() => refetchActivityItems()}
          setOpen={() => {
            setManualValidationModal({
              open: false,
              _id: undefined,
              activityItemCode: undefined,
              fieldglassId: undefined,
            });
            refetchActivityItems();
          }}
          workItemID={manualValidationModal._id}
          activityItemCode={manualValidationModal.activityItemCode}
          fieldglassId={manualValidationModal.fieldglassId}
        />
      )}

      {reviewModalOpen && (
        <ConfirmReviewActivityModal
          isUploading={isReviewActivityItem} //Habilitar quando a nova rota estiver disponível
          modalOpen={reviewModalOpen}
          onClose={() => {
            setReviewModalOpen(false);
            refetchActivityItems();
          }}
          onConfirm={handleReviewActivityValidation}
          // data-cy="expiration-date-modal"
        />
      )}

      {newCheckDocumentsModal.open && (
        <NewModalDocuments
          modalOpen={newCheckDocumentsModal.open}
          onClose={() =>
            setNewCheckDocumentsModal((fallback) => ({
              ...fallback,
              open: false,
            }))
          }
        />
      )}

      {newAnalysisModal.open && (
        <NewModalAnalysis
          modalOpen={newAnalysisModal.open}
          onClose={() =>
            setNewAnalysisModal((fallback) => ({
              ...fallback,
              open: false,
            }))
          }
        />
      )}

      <Card>
        <CardHeader color="rose" icon>
          <CardIcon color="rose">
            <Group />
          </CardIcon>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <h4
              className={classes.cardIconTitle}
              data-cy="activity-items-card-title"
            >
              {t("activityItems")}
            </h4>
            {shouldRenderToggleButtonGroup && (
              <ToggleButtonGroup
                sx={{ paddingTop: 1 }}
                color="primary"
                value={alignment}
                size="small"
                exclusive
                onChange={handleToggle}
                aria-label="Enable or Disable Profile"
              >
                <Tooltip title="Enable Profile View">
                  <ToggleButton value="enabled" aria-label="enable profile">
                    <Ballot style={{ fontSize: "1.2rem" }} />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Enable List View">
                  <ToggleButton value="disabled" aria-label="disable profile">
                    <MoreVert style={{ fontSize: "1.2rem" }} />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            )}
          </Box>
        </CardHeader>
        <CardBody>
          {isActivityItemsLoading ? (
            <CircularProgress data-cy="activity-items-loading-spinner" />
          ) : (
            <ReactTableWrapper
              columns={columnsStatus}
              data={formattedActivityItems || []}
              filters={filters}
              setFilters={setFilters}
              sortingInitial={sortingInitial}
              setSortingInitial={setSortingInitial}
              cacheFilters={true}
              data-cy="activity-items-react-table"
              id="activity-items-react-table"
              columnsNotTrimFilters={["action"]}
            />
          )}
        </CardBody>
      </Card>
    </GridItemTS>
  );
}
